import React, { FC, useMemo } from 'react';
import { useIntl } from "react-intl";
import { useHistory } from "react-router-dom";
import { formatPrice } from '../../../selectors';
import { AbilityModel } from '../../../models/AbilityModel';
import { HeaderModel } from '../../../models/CommonModel';
import SortableColumn from '../../../components/Table/SortableColumn';
import moment from 'moment';

type Props = {
    data: Array<AbilityModel>
    handleExportPdf: (id: number) => void,
    header: HeaderModel[],
    hideCols: string[],
    openColConfig: () => void,
    handleEditItem: (item: AbilityModel) => void,
    handleSort: (sortBy: string, direction: 'asc' | 'desc' | 'none') => void,
    hideConfig?: boolean,
    handleCheck: (e: any, item: AbilityModel) => void
    handleCheckAll: (e: any) => void,
    selectedIds: number[]
    canEdit: boolean
};

const PaymentVoucherTable: FC<Props> = ({ 
    data, 
    handleExportPdf, 
    header, 
    handleCheckAll, 
    selectedIds, 
    hideCols, 
    handleEditItem, 
    openColConfig, 
    handleSort, 
    hideConfig,
    handleCheck,
    canEdit
}) => {
    const intl = useIntl();
    const history = useHistory();
    const paymentMethod = (method: string) => {
        if (method == 'banking') {
            return 'Chuyển khoản';
        } else if (method == 'cash') {
            return 'Tiền mặt';
        }
        return '';
    }

    const renderGroup = (expenseGroup: string | null, purpose: string | null) => {
        if (purpose && purpose === 'tax') {
            return 'Thuế';
        }

        switch (expenseGroup) {
            case 'payroll':
                return renderPayrollPurpose(purpose);
            case 'electric':
                return 'Chi phí điện';
            case 'water':
                return 'Chi phí nước';
            case 'telecom':
                return 'Chi phí viễn thông';
            case 'rent':
                return 'Chi phí thuê kho bãi, mặt bằng KD';
            case 'manage':
                return 'Chi phí quản lý (chi phí VPP, công cụ, dụng cụ, lệ phí môn bài ..)';
            case 'depreciation':
                return 'Chi phí khấu hao tài sản cố định phục vụ cho hoạt động sản xuất, kinh doanh.';
            case 'interest':
                return 'Chi phí trả lãi tiền vay vốn sản xuất, kinh doanh.';
            case 'other':
                return renderOtherPurpose(purpose ?? 'other', expenseGroup)
        }

        if (purpose) {
            return renderOtherPurpose(purpose, expenseGroup)
        }
        return 'Công nợ';
    }

    const renderOtherPurpose = (purpose: string | null, expenseGroup: string | null) => {
        if (purpose === 'expenses' && expenseGroup === 'other') {
            return 'Chi phí khác (hội nghị, công tác phí, thanh lý, nhượng bán TSCĐ, thuê ngoài khác,...)';
        }
        switch (purpose) {
            case 'deposit':
                return 'Nộp tiền ngân hàng';
            case 'withdraw':
                return 'Rút tiền mặt';
            case 'bank_fee':
                return 'Phí ngân hàng';
            case 'personal_used':
                return 'Chi tiêu cá nhân';
            case 'sales':
            case 'expenses':
                return 'Công nợ';
            default:
                return 'Chi phí khác (hội nghị, công tác phí, thanh lý, nhượng bán TSCĐ, thuê ngoài khác,...)';
        }
    }

    const renderPayrollPurpose = (purpose: string | null) => {
        switch (purpose) {
            case 'salary':
                return 'Chi phí nhân sự: Lương';
            case 's_insurance':
                return 'Chi phí nhân sự: BHXH';
            case 'h_insurance':
                return 'Chi phí nhân sự: BHYT';
            case 'u_insurance':
                return 'Chi phí nhân sự: BHTN';
        }
        return 'Chi phí nhân sự';
    }

    const isChecked = (id: number) => selectedIds.includes(id);
    const headerFiels = header?.map((e) => e.field) ?? [];
    const isCheckedAll = data.length > 0 && data.every(item => selectedIds.includes(item.id));

    return (
        <div className='table-responsive'>
            <table className="table table-hover table-bordered table-striped border gy-7 gs-7">
                <thead>
                    <tr className="fw-bold fs-6 text-gray-800 border-bottom-2 border-gray-200">
                        {!hideConfig && (
                            <th>
                                <i
                                    className="fa fa-cogs"
                                    style={{ cursor: 'pointer' }}
                                    onClick={openColConfig}
                                ></i>
                            </th>
                        )}
                        <th>
                            <input  className="form-check-input" type="checkbox"
                                checked={isCheckedAll}
                                onChange={handleCheckAll}
                            />
                        </th>
                        {header.map((th, index) => {
                            if (!hideCols.includes(th.field)) {
                                if (th.sortable) {
                                    return (
                                        <SortableColumn
                                            key={'th-' + index}
                                            sortBy={th?.field}
                                            onSortChange={handleSort}
                                            style={th?.style ?? {}}
                                        >
                                            {th.label}
                                        </SortableColumn>
                                    )
                                }
                                return (
                                    <th style={th?.style ?? {}}>{th.label}</th>
                                )
                            }
                        })}
                        <th></th>
                    </tr>
                </thead>
                <tbody>
                    {data?.map((item: AbilityModel, index: number) => {
                        const checked = isChecked(item.id);
                        const invoices = item.order?.invoices ?? (item.receipt?.invoices ?? [])
                        const lastInvoice = invoices?.length ? invoices[invoices.length - 1] : null;
                        let invoiceStr = null;
                        if (lastInvoice) {
                            if (item.ref_order_id) {
                                invoiceStr = lastInvoice.invoice_template_code+lastInvoice.invoice_no
                            } else {
                                invoiceStr = lastInvoice.label
                            }
                        }

                        let note = item.note ?? '';
                        if (invoiceStr && !note.includes(invoiceStr)) {
                            note += ' | HĐ: ' + invoiceStr
                        }
                        let prefix = 'PC';
                        if (item.payment_method === 'banking') {
                            prefix = 'CNH';
                        }

                        return (
                            <tr key={`ability_${index}`} className="cursor-pointer">
                                {!hideConfig && (
                                <td></td>
                                )}
                                <td>
                                    <div className="form-check form-check-custom">
                                        <input key={`item_checkbox_${item.id}_${checked}`} className="form-check-input" type="checkbox"
                                            value={item.id}
                                            defaultChecked={checked}
                                            onChange={(e) => handleCheck(e, item)}/>
                                    </div>
                                </td>
                                {(headerFiels.includes('id') && !hideCols.includes('id')) && (
                                <td>{prefix}{item.id}</td>
                                )}
                                {!hideCols.includes('created_at') && (
                                <td>{moment(item.created_at).format('DD-MM-YYYY')}</td>
                                )}
                                {!hideCols.includes('date') && (
                                <td>{item.date ?? ''}</td>
                                )}
                                {!hideCols.includes('name') && (
                                <td>{item.name}</td>
                                )}
                                {!hideCols.includes('bank_id') && (
                                    <td className='text-center'>{item?.bank ? item.bank.bank_name + ' - ' + item.bank.bank_account_number : ''}</td>
                                )}
                                {!hideCols.includes('note') && (
                                <td>{note}</td>
                                )}
                                {!hideCols.includes('value') && (
                                <td>{formatPrice(item.value)}</td>
                                )}
                                {!hideCols.includes('expense_group') && (
                                <td>
                                    { renderGroup(item.expense_group ?? null, item.purpose ?? null) }
                                </td>
                                )}
                                {!hideCols.includes('payment_method') && (
                                <td>
                                    { paymentMethod(item.payment_method ?? '') }
                                </td>
                                )}
                                {!hideCols.includes('doc_label') && (
                                <td>
                                    {item.doc_path ? (
                                        <a href={item?.doc_path} target="__blank">{item?.doc_label ?? 'Xem'}</a>
                                    ) : (
                                        <>{item?.doc_label}</>
                                    )}
                                </td>
                                )}
                                <td>
                                    {canEdit && (
                                        <button 
                                            className="btn btn-primary me-3 btn-sm btn-icon" 
                                            type="button"
                                            onClick={() => handleEditItem(item)}
                                        >
                                            <i className='bi bi-pencil-square'></i>
                                        </button>
                                    )}
                                    <button 
                                        className="btn btn-primary me-3 btn-sm" 
                                        type="button"
                                        onClick={() => handleExportPdf(item.id)}
                                    >
                                        {intl.formatMessage({id: 'ORDER.PRINT'})}
                                    </button>
                                </td>
                            </tr>
                        )
                    })}
                </tbody>
            </table>
        </div>
    );
}

export default PaymentVoucherTable;